import React, {
  useCallback, useEffect, useMemo,
  useState,
} from 'react';
import * as Styles from './NewHomeLayoutHandler.styles';
import useLocale from '@/hooks/useLocale';
import { Skeleton } from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  emailsActions, linkNaBioActions, productsActions, sitesActions, domainsActions, preChatActions,
} from '@/redux/modules';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING,
  ATIVACAO_RESGATE_DOMINIO_GRATUITO,
  ATIVACAO_NEW_HOME_PRODUCTS_OFFER,
} from '@/config/GrowthBook/constants';
import Banner from '../Banner';
import DomainOffer from '../DomainOffer';
import { Services } from '../Services';
import { SERVICES_IDS } from '../Services/Services.enum';
import { HOSTING_URL } from '@/config/urls/hostingUrls';
import { GATOR_PROTECT_URL } from '@/config/urls/gatorprotect';
import { OPEN_CLAW_URL } from '@/config/urls/openClaw';
import { N8N_URL } from '@/config/urls/n8nUrls';
import { DEDICATED_LINUX_SERVER_URL } from '@/config/urls/dedicatedLinuxServerUrls';
import { homeAnalytics } from '@/analytics/home';
import { UsefulLinks } from '../UsefulLinks';
import { ModalRegisterDomain } from '../ModalRegisterDomain';
import { EssentialTools } from '../EssentialTools';
import { ESSENTIAL_TOOLS_IDS } from '../EssentialTools/EssentialTools.enum';
import { ProductsOffer } from '../ProductsOffer';
import {
  PRODUCTS_OFFER_IDS, PRODUCTS_OFFER_PID,
} from '../ProductsOffer/ProductsOffer.enum';
import { BLOG_URL, HELP_CENTER_URL } from '@/config/urls/supportUrls';
import homeActions from '@/redux/modules/home/home.actions';
import { isBRServer } from '@/utils/Validators/validation';

const NewHomeLayoutHandler = () => {
  const [showModalRegisterDomain, setShowModalRegisterDomain] = useState(false);

  const enableAgentFactoryNewOnboarding = useFeatureIsOn(PORTFOLIO_AGENT_FACTORY_NEW_ONBOARDING);
  const showRedeemFreeDomain = useFeatureIsOn(ATIVACAO_RESGATE_DOMINIO_GRATUITO);
  const newHomeProductsOffer = useFeatureValue(ATIVACAO_NEW_HOME_PRODUCTS_OFFER);

  const history = useHistory();
  const dispatch = useDispatch();
  const { routes: routesLocale, home: homeLocale } = useLocale();

  const { owned: ownedProducts, loading: productsLoading } = useSelector(state => state.products);
  const { profiles: linkNaBioProfiles, loading: linkNaBioLoading } = useSelector(state => state.linkNaBio);
  const { loadingProEmails: emailsLoading } = useSelector(state => state.emails);
  const { productsList } = useSelector(state => state.preChat);
  const { productsPriceData: productsOfferData } = useSelector(state => state.home);
  const summaryState = useSelector(state => state.summary);
  const {
    list: sitesList,
    loading: sitesLoading,
    domainsWithSitesCreated,
    loadingDomainsWithSitesCreated,
  } = useSelector(state => state.sites);
  const { redeemAvailability } = useSelector(state => state.newDomains);

  const hasSitesCreatedBase = useMemo(() => !!domainsWithSitesCreated?.length, [domainsWithSitesCreated?.length]);
  const hasHosting = useMemo(() => ownedProducts?.some(product => ['shared', 'vps', 'dedicated'].includes(product.category) && !!product.has_product), [ownedProducts]);
  const hasDomain = useMemo(() => ownedProducts?.some(product => product.category === 'domain' && !!product.has_product), [ownedProducts]);
  const hasSites = useMemo(() => !!sitesList?.length, [sitesList]);
  const hasEmail = useMemo(() => ownedProducts?.some(product => product.category === 'proemail' && !!product.has_product), [ownedProducts]);
  const hasLinkNaBio = useMemo(() => !!linkNaBioProfiles?.length, [linkNaBioProfiles]);
  const hasSitesCreated = hasSitesCreatedBase;
  const hasSiteLock = useMemo(() => ownedProducts?.some(product => product.category === 'sitelock' && !!product.has_product), [ownedProducts]);
  const sitelockProductId = productsList?.hostings?.find(product => product.name.toLowerCase().includes('sitelock'))?.id;
  const hasGatorProtect = !!productsList?.hostings?.find(product => product.name.toLowerCase().includes('gator protect') && product.status.toLowerCase() === 'active');
  const gatorProtectEnabled = !!summaryState?.features?.gator_protect;

  const redeemAvailabilityDomainsLength = redeemAvailability?.domains?.length || 0;
  const showFreeDomainCard = redeemAvailabilityDomainsLength > 0 && showRedeemFreeDomain;

  const loadingData = useMemo(() => loadingDomainsWithSitesCreated
    || sitesLoading
    || productsLoading
    || linkNaBioLoading
    || emailsLoading, [
    loadingDomainsWithSitesCreated,
    sitesLoading,
    productsLoading,
    linkNaBioLoading,
    emailsLoading,
  ]);

  const handleCreateSiteWithAi = useCallback(() => {
    homeAnalytics.homeBannerClick({ label: 'Seu site criado com IA' });
    history.push(`${routesLocale.sitesPage}${routesLocale.sitesCreate}?isIaBuilder=true`);
  }, [history, routesLocale]);

  const handleSearchDomain = useCallback((domainSearchValue = '') => {
    const queryParams = new URLSearchParams();
    queryParams.set('domain', domainSearchValue);

    history.push(`${routesLocale.domains}${routesLocale.registerDomain}?${queryParams.toString()}`);
  }, [history, routesLocale]);

  const newHomeLayoutRequests = useCallback(() => {
    dispatch(sitesActions.domains.requestDomainsWithSites());
    dispatch(productsActions.products.request({ history: null }));
    dispatch(linkNaBioActions.profile.get.request());
    dispatch(emailsActions.emails.request());
    dispatch(sitesActions.sites.request());

    if (hasHosting && hasSitesCreated) {
      dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: false }));
      dispatch(homeActions.userProductsPrice.request({
        [PRODUCTS_OFFER_IDS.VPS_OPEN_CLAW]: {
          pid: PRODUCTS_OFFER_PID.VPS_OPEN_CLAW,
          type: PRODUCTS_OFFER_IDS.VPS_OPEN_CLAW,
          discount: newHomeProductsOffer?.[isBRServer ? 'br' : 'es']?.[PRODUCTS_OFFER_IDS.VPS_OPEN_CLAW],
        },
        [PRODUCTS_OFFER_IDS.VPS_N8N]: {
          pid: PRODUCTS_OFFER_PID.VPS_N8N,
          type: PRODUCTS_OFFER_IDS.VPS_N8N,
          discount: newHomeProductsOffer?.[isBRServer ? 'br' : 'es']?.[PRODUCTS_OFFER_IDS.VPS_N8N],
        },
        [PRODUCTS_OFFER_IDS.DEDICATED_LINUX_SERVER]: {
          pid: PRODUCTS_OFFER_PID.DEDICATED_LINUX_SERVER,
          type: PRODUCTS_OFFER_IDS.DEDICATED_LINUX_SERVER,
          discount: newHomeProductsOffer?.[isBRServer ? 'br' : 'es']?.[PRODUCTS_OFFER_IDS.DEDICATED_LINUX_SERVER],
        },
      }));
    }
    enableAgentFactoryNewOnboarding && dispatch(sitesActions.agentsFactory.account.request());
  }, [dispatch, enableAgentFactoryNewOnboarding, hasHosting, hasSitesCreated, newHomeProductsOffer]);

  useEffect(() => {
    newHomeLayoutRequests();
  }, [newHomeLayoutRequests]);

  useEffect(() => {
    if (showRedeemFreeDomain && !redeemAvailability?.loaded) {
      dispatch(domainsActions.redeemAvailability.request());
    }
  }, [dispatch, showRedeemFreeDomain, redeemAvailability]);

  const servicesProps = useCallback(() => {
    const cardsActions = {
      [SERVICES_IDS.HOSTING]: {
        onClick: () => {
          homeAnalytics.homeBannerClick({ label: 'Adquirir minha hospedagem' });
          window.open(HOSTING_URL, '_blank');
        },
      },
      [SERVICES_IDS.GATOR_PROTECT]: {
        onClick: () => {
          homeAnalytics.homeBannerClick({ label: 'Quero proteger meu site' });
          window.open(GATOR_PROTECT_URL, '_blank');
        },
      },
      [SERVICES_IDS.OPEN_CLAW]: {
        onClick: () => {
          homeAnalytics.homeBannerClick({ label: 'OpenClaw-Saiba mais' });
          window.open(OPEN_CLAW_URL, '_blank');
        },
      },
      [SERVICES_IDS.EMAIL]: {
        onClick: () => {
          homeAnalytics.homeBannerClick({ label: 'Email com o Nome da Marca' });
          hasDomain ? history.push(routesLocale.emails) : setShowModalRegisterDomain(true);
        },
      },
    };

    return {
      servicesLocale: homeLocale?.v2?.services,
      cardsActions,
      hasHosting,
    };
  }, [history, routesLocale, homeLocale, hasHosting, hasDomain, setShowModalRegisterDomain]);

  const usefulLinksProps = useCallback(() => {
    const usefulLinksActions = {
      courses: () => {
        homeAnalytics.usefulLinksCoursesClick();
        history.push(`${routesLocale.courses}`);
      },
      blog: () => {
        homeAnalytics.usefulLinksBlogClick();
        window.open(BLOG_URL, '_blank');
      },
      help: () => {
        homeAnalytics.usefulLinksHelpClick();
        window.open(HELP_CENTER_URL, '_blank');
      },
    };

    return {
      locale: homeLocale?.v2?.usefulLinks,
      cardsActions: usefulLinksActions,
    };
  }, [homeLocale, history, routesLocale]);

  const essentialToolsCards = useMemo(() => {
    const cards = homeLocale?.v2?.essentialTools?.cards;
    if (!cards) {
      return {};
    }

    let securityCard = {};

    if (hasSiteLock) {
      securityCard = {
        [ESSENTIAL_TOOLS_IDS.SITE_LOCK]: {
          id: ESSENTIAL_TOOLS_IDS.SITE_LOCK,
          cta: {
            primary: {
              label: cards[ESSENTIAL_TOOLS_IDS.SITE_LOCK].cta.secondary,
              shouldShow: true,
              onClick: () => history.push(`${routesLocale.security}${routesLocale.productdetail}/standalone/${sitelockProductId}`),
              variant: 'secondary',
            },
          },
        },
      };
    } else if (gatorProtectEnabled) {
      securityCard = {
        [ESSENTIAL_TOOLS_IDS.GATOR_PROTECT]: {
          id: ESSENTIAL_TOOLS_IDS.GATOR_PROTECT,
          cta: {
            primary: {
              label: cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.primary,
              shouldShow: hasGatorProtect,
              onClick: () => {
                homeAnalytics.readyProductsActivationCardClick({
                  label: cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.primary,
                });
                return history.push(routesLocale.gatorProtect);
              },
            },
            secondary: {
              label: cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.secondary,
              shouldShow: !hasGatorProtect,
              onClick: () => {
                const isTrialCta = cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.secondary === cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.tertiary;
                homeAnalytics.readyProductsActivationCardClick({
                  label: isTrialCta
                    ? cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.tertiary
                    : cards[ESSENTIAL_TOOLS_IDS.GATOR_PROTECT].cta.secondary,
                });
                return history.push(routesLocale.gatorProtect);
              },
            },
          },
        },
      };
    }

    return {
      [ESSENTIAL_TOOLS_IDS.EMAIL]: {
        id: ESSENTIAL_TOOLS_IDS.EMAIL,
        cta: {
          primary: {
            label: cards[ESSENTIAL_TOOLS_IDS.EMAIL].cta.primary,
            shouldShow: true,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.EMAIL].cta.primary,
              });
              return hasDomain
                ? history.push(`${routesLocale?.domains}${routesLocale?.buyDomain}`)
                : setShowModalRegisterDomain(true);
            },
          },
          secondary: {
            label: cards[ESSENTIAL_TOOLS_IDS.EMAIL].cta.secondary,
            shouldShow: hasEmail,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.EMAIL].cta.secondary,
              });
              return history.push(routesLocale.emails);
            },
          },
        },
      },
      [ESSENTIAL_TOOLS_IDS.LINK_IN_BIO]: {
        id: ESSENTIAL_TOOLS_IDS.LINK_IN_BIO,
        cta: {
          primary: {
            label: cards[ESSENTIAL_TOOLS_IDS.LINK_IN_BIO].cta.primary,
            shouldShow: true,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.LINK_IN_BIO].cta.primary,
              });
              return history.push(routesLocale.linkNaBio);
            },
          },
          secondary: {
            label: cards[ESSENTIAL_TOOLS_IDS.LINK_IN_BIO].cta.secondary,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.LINK_IN_BIO].cta.secondary,
              });
              return history.push(routesLocale.linkNaBio);
            },
            shouldShow: hasLinkNaBio,
          },
        },
      },
      [ESSENTIAL_TOOLS_IDS.DOMAIN]: {
        id: ESSENTIAL_TOOLS_IDS.DOMAIN,
        cta: {
          primary: {
            label: cards[ESSENTIAL_TOOLS_IDS.DOMAIN].cta.primary,
            shouldShow: true,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.DOMAIN].cta.primary,
              });
              return history.push(`${routesLocale?.domains}${routesLocale?.buyDomain}`);
            },
          },
          secondary: {
            label: cards[ESSENTIAL_TOOLS_IDS.DOMAIN].cta.secondary,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.DOMAIN].cta.secondary,
              });
              return history.push(routesLocale.domains);
            },
            shouldShow: hasDomain,
          },
        },
      },
      [ESSENTIAL_TOOLS_IDS.SITE_BUILDER]: {
        id: ESSENTIAL_TOOLS_IDS.SITE_BUILDER,
        cta: {
          primary: {
            label: cards[ESSENTIAL_TOOLS_IDS.SITE_BUILDER].cta.primary,
            shouldShow: true,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.SITE_BUILDER].cta.primary,
              });
              return history.push(`${routesLocale?.sitesPage}${routesLocale?.sitesCreate}?isIaBuilder=true`);
            },
          },
          secondary: {
            label: cards[ESSENTIAL_TOOLS_IDS.SITE_BUILDER].cta.secondary,
            shouldShow: true,
            onClick: () => {
              homeAnalytics.readyProductsActivationCardClick({
                label: cards[ESSENTIAL_TOOLS_IDS.SITE_BUILDER].cta.secondary,
              });
              return history.push(routesLocale.sitesPage);
            },
          },
        },
      },
      ...securityCard,
    };
  }, [
    homeLocale,
    routesLocale,
    history,
    hasDomain,
    hasEmail,
    hasSiteLock,
    hasLinkNaBio,
    hasGatorProtect,
    gatorProtectEnabled,
    sitelockProductId,
  ]);

  const essentialToolsActivationById = useMemo(() => ({
    [ESSENTIAL_TOOLS_IDS.DOMAIN]: hasDomain,
    [ESSENTIAL_TOOLS_IDS.EMAIL]: hasEmail,
    [ESSENTIAL_TOOLS_IDS.GATOR_PROTECT]: hasGatorProtect,
    [ESSENTIAL_TOOLS_IDS.LINK_IN_BIO]: hasLinkNaBio,
    [ESSENTIAL_TOOLS_IDS.SITE_BUILDER]: hasSites,
    [ESSENTIAL_TOOLS_IDS.SITE_LOCK]: hasSiteLock,
  }), [hasDomain, hasEmail, hasGatorProtect, hasLinkNaBio, hasSites, hasSiteLock]);

  const essentialToolsProps = useCallback(() => ({
    locale: homeLocale?.v2?.essentialTools,
    cardsActions: essentialToolsCards,
    activationById: essentialToolsActivationById,
  }), [homeLocale, essentialToolsCards, essentialToolsActivationById]);

  const modalRegisterDomainProps = useCallback(() => {
    const goToBuyDomain = () => history.push(`${routesLocale?.domains}${routesLocale?.buyDomain}`);

    return {
      goToBuyDomain,
      setShowModalRegisterDomain,
    };
  }, [routesLocale, setShowModalRegisterDomain, history]);

  const productsOfferProps = useCallback(() => {
    const cardsActions = {
      [PRODUCTS_OFFER_IDS.VPS_OPEN_CLAW]: {
        onClick: () => {
          homeAnalytics.potencializeOfferCardClick({
            label: homeLocale?.v2?.productsOffer?.products?.[PRODUCTS_OFFER_IDS.VPS_OPEN_CLAW]?.name,
          });
          window.open(OPEN_CLAW_URL, '_blank');
        },
      },
      [PRODUCTS_OFFER_IDS.VPS_N8N]: {
        onClick: () => {
          homeAnalytics.potencializeOfferCardClick({
            label: homeLocale?.v2?.productsOffer?.products?.[PRODUCTS_OFFER_IDS.VPS_N8N]?.name,
          });
          window.open(N8N_URL, '_blank');
        },
      },
      [PRODUCTS_OFFER_IDS.DEDICATED_LINUX_SERVER]: {
        onClick: () => {
          homeAnalytics.potencializeOfferCardClick({
            label: homeLocale?.v2?.productsOffer?.products?.[PRODUCTS_OFFER_IDS.DEDICATED_LINUX_SERVER]?.name,
          });
          window.open(DEDICATED_LINUX_SERVER_URL, '_blank');
        },
      },
    };

    return {
      locale: homeLocale?.v2?.productsOffer,
      cardsActions,
      productsOfferData,
      newHomeProductsOffer,
    };
  }, [homeLocale, productsOfferData, newHomeProductsOffer]);

  if (loadingData) {
    return (
      <>
        <Styles.WhiteCard $loading data-testid="loadingState">
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonRow>
            <Skeleton width="100%" height="400px" />
          </Styles.SkeletonRow>
        </Styles.WhiteCard>

        <Styles.WhiteCard $loading>
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonInline>
            <Styles.SkeletonRow>
              <Skeleton width="100%" height="400px" />
            </Styles.SkeletonRow>

            <Styles.SkeletonRow>
              <Skeleton width="100%" height="400px" />
            </Styles.SkeletonRow>
          </Styles.SkeletonInline>
        </Styles.WhiteCard>

        <Styles.WhiteCard $loading>
          <Styles.SkeletonRow>
            <Skeleton width="100%" height="20px" />
          </Styles.SkeletonRow>

          <Styles.SkeletonRow>
            <Skeleton width="100%" height="300px" />
          </Styles.SkeletonRow>
        </Styles.WhiteCard>
      </>
    );
  }

  return (
    <Styles.Wrapper data-testid="home-layout-handler">
      <Styles.WhiteCard>
        <Styles.WhiteCardContent>
          {hasHosting && !hasSitesCreated && (
            <Banner
              locale={homeLocale.v2.banner}
              onCreateSiteWithAi={handleCreateSiteWithAi}
            />
          )}

          {hasHosting && hasSitesCreated ? (
            <>
              {showFreeDomainCard && (
                <DomainOffer
                  locale={homeLocale.v2.freeDomainCard}
                  onSearchDomain={handleSearchDomain}
                />
              )}

              <EssentialTools {...essentialToolsProps()} />
            </>
          ) : (
            <Services {...servicesProps()} />
          )}
        </Styles.WhiteCardContent>
      </Styles.WhiteCard>

      {hasHosting && hasSitesCreated && (
        <ProductsOffer {...productsOfferProps()} />
      )}

      <UsefulLinks {...usefulLinksProps()} />

      {showModalRegisterDomain && (
        <ModalRegisterDomain {...modalRegisterDomainProps()} />
      )}
    </Styles.Wrapper>
  );
};

export default NewHomeLayoutHandler;
